import React, { useState } from 'react';
import { toast } from 'sonner';
import { Timer, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const goldBtn = 'w-full h-12 bg-amber-400 hover:bg-amber-300 text-[#00247D] font-black text-base';
const ghostBtn = 'w-full h-10 text-white/70 hover:text-white hover:bg-white/5';

function JoinCodeNotice({ joinCode, onCopy }) {
  return (
    <div className="rounded-lg bg-amber-400/10 border border-amber-400/40 p-3 sm:p-4 mb-5" data-testid="period-setup-join-notice">
      <div className="text-amber-200 text-xs font-bold tracking-wide uppercase mb-1">Sharing stats with another device?</div>
      <p className="text-white/85 text-sm leading-snug">
        If you have other statisticians joining, please have them go to{' '}
        <span className="font-semibold text-amber-200">scheduleguru.ws/join</span>{' '}
        and enter the game code to take stats for this game.
      </p>
      {joinCode && (
        <div className="mt-3 flex items-center justify-between rounded-md bg-black/30 border border-white/15 px-3 py-2">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-white/50 font-bold">Game code</div>
            <div className="text-white font-black text-xl tracking-wider" data-testid="period-setup-join-code">{joinCode}</div>
          </div>
          <Button size="sm" variant="outline" onClick={onCopy}
            className="border-amber-400/60 text-amber-200 hover:bg-amber-400/20 hover:text-white"
            data-testid="period-setup-copy-code">
            Copy
          </Button>
        </div>
      )}
    </div>
  );
}

// Shown right after both starting lineups are set on a fresh game.
export function PeriodSetupModal({ initialMinutes, joinCode, onCopyJoinCode, onConfirm }) {
  const [minutes, setMinutes] = useState(String(initialMinutes));
  const confirm = () => {
    const mins = parseInt(minutes, 10);
    if (!Number.isFinite(mins) || mins < 1) {
      toast.error('Enter a period length of at least 1 minute');
      return;
    }
    onConfirm(mins);
  };
  return (
    <div className="fixed inset-0 z-[112] bg-black/60 flex items-center justify-center p-4" data-testid="period-setup-overlay">
      <div className="w-full max-w-md bg-[#001A5C] rounded-2xl border border-white/10 p-5 sm:p-6" data-testid="period-setup-modal">
        <div className="flex items-center gap-2 mb-3">
          <Timer className="w-5 h-5 text-amber-400" />
          <div className="text-white text-lg font-bold">Set period length</div>
        </div>
        <p className="text-white/70 text-sm mb-3">How many minutes per quarter for this game?</p>
        <div className="flex items-center gap-2 mb-5">
          <input type="number" min="1" inputMode="numeric" value={minutes}
            onChange={(e) => setMinutes(e.target.value.replace(/[^0-9]/g, ''))}
            className="w-24 h-12 text-center text-2xl font-bold bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-400"
            data-testid="period-setup-minutes"
            autoFocus />
          <span className="text-white/80 font-medium">minutes / quarter</span>
        </div>
        <JoinCodeNotice joinCode={joinCode} onCopy={onCopyJoinCode} />
        <Button onClick={confirm} className="w-full h-11 bg-amber-400 hover:bg-amber-300 text-[#00247D] font-black text-base" data-testid="period-setup-confirm">
          OK, let's play
        </Button>
      </div>
    </div>
  );
}

const endTitle = (q) => (q > 4 ? `End of OT${q - 4}` : q === 4 ? 'End of the 4th' : `End of Q${q}`);

// Fires once when the running clock hits 0:00.
export function TimerExpiredModal({ endedQuarter: q, quarterLengthMin, scoreA, scoreB, onAdvance, onDismiss, onEndGame }) {
  const regulation = q < 4;
  return (
    <div className="fixed inset-0 z-[113] bg-black/70 flex items-center justify-center p-4" data-testid="timer-expired-overlay">
      <div className="w-full max-w-md bg-[#001A5C] rounded-2xl border border-amber-400/40 p-5 sm:p-6" data-testid="timer-expired-modal">
        <div className="flex items-center gap-2 mb-2">
          <Timer className="w-5 h-5 text-amber-400" />
          <div className="text-white text-lg font-bold">{endTitle(q)}</div>
        </div>
        <p className="text-white/70 text-sm mb-5">
          {regulation
            ? `The game clock hit 0:00. Ready to start Q${q + 1}? The clock will reset to ${quarterLengthMin} minutes.`
            : `Score is ${scoreA}–${scoreB}. Do you want to end the game here or head to overtime?`}
        </p>
        <div className="flex flex-col gap-2">
          {regulation ? (
            <Button onClick={onAdvance} className={goldBtn} data-testid="timer-expired-next-quarter">
              Move to Q{q + 1}
            </Button>
          ) : (
            <>
              <Button onClick={onAdvance} className={goldBtn} data-testid="timer-expired-overtime">
                Go to {q > 4 ? `OT${q - 3}` : 'Overtime'}
              </Button>
              <Button onClick={onEndGame} variant="outline"
                className="w-full h-12 border-white/30 text-white hover:bg-white/10 font-bold text-base"
                data-testid="timer-expired-end-game">
                End the Game
              </Button>
            </>
          )}
          <Button variant="ghost" onClick={onDismiss} className={ghostBtn} data-testid="timer-expired-dismiss">
            Not yet — keep tracking
          </Button>
        </div>
      </div>
    </div>
  );
}

const clockInput = 'w-20 bg-[#0A0F2C] text-white text-3xl font-mono tabular-nums text-center rounded-md border border-white/20 focus:border-amber-400 focus:outline-none py-2';

// Manual clock set; seeded from the current remaining time when opened.
export function ClockEditModal({ clock, onClose, onSave }) {
  const baseSec = Math.max(0, clock.remaining_sec ?? clock.quarter_length_sec ?? 600);
  const [min, setMin] = useState(String(Math.floor(baseSec / 60)));
  const [sec, setSec] = useState((baseSec % 60).toString().padStart(2, '0'));
  const save = (alsoSetLength) => {
    const totalSec = Math.max(0, (parseInt(min, 10) || 0) * 60 + (parseInt(sec, 10) || 0));
    onSave(totalSec, alsoSetLength);
  };
  return (
    <div className="fixed inset-0 z-[112] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="clock-edit-overlay">
      <div className="w-full max-w-xs bg-[#001A5C] rounded-2xl border border-white/10 p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-bold flex items-center gap-1.5"><Timer className="w-4 h-4" /> Set Clock</h3>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="clock-edit-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex items-center justify-center gap-2 mb-4">
          <input type="number" min="0" max="99" value={min} onChange={(e) => setMin(e.target.value)}
            className={clockInput} data-testid="clock-edit-min" />
          <span className="text-white text-3xl font-bold">:</span>
          <input type="number" min="0" max="59" value={sec} onChange={(e) => setSec(e.target.value)}
            onBlur={(e) => setSec((parseInt(e.target.value, 10) || 0).toString().padStart(2, '0'))}
            className={clockInput} data-testid="clock-edit-sec" />
        </div>
        <div className="space-y-2">
          <Button onClick={() => save(false)} className="w-full bg-amber-400 hover:bg-amber-300 text-[#00247D] font-bold" data-testid="clock-edit-save-btn">
            Set Clock to {min || 0}:{(sec || '0').padStart(2, '0')}
          </Button>
          <Button onClick={() => save(true)} variant="outline" className="w-full border-white/20 text-white hover:bg-white/10" data-testid="clock-edit-save-as-length-btn">
            Set & Use as Quarter Length
          </Button>
        </div>
      </div>
    </div>
  );
}
